<template>
  <app-notification
    v-if="isNotificationShown"
    :active-notification="isNotificationShown"
    :main-text="notificationMessage"
    :is-add-to-cart-notification="true"
    class="add-to-cart-notification"
    @open-cart="onOpenCart"
  >
    <template #additional-content>
      <delivery-notification
        v-if="!hasFreeDelivery"
        :free-delivery-message="freeDeliveryMessage"
        class="delivery-notification"
      />
    </template>
  </app-notification>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';

import DeliveryNotification from '@/components/cart/DeliveryNotification.vue';

import { priceFormatterMixin } from '@/mixins/priceFormatterMixin';
import { NotificationService } from '@/services/NotificationService';
import { displayNameMixin } from '@/mixins/displayNameMixin';

import { types } from '@/store/types';
import { EYE_SERUM_GENERIC_FORMULA_LABELS, PRODUCT_LABEL } from '@/constants/treatment';
import { LONG_NOTIFICATION_DURATION } from '@/constants/common';

export default {
  name: 'AddToCartNotification',
  components: {
    AppNotification: () => import('@/components/common/AppNotification.vue'),
    DeliveryNotification
  },
  mixins: [priceFormatterMixin, displayNameMixin],
  data() {
    return {
      addToCartNotificationTimeout: null,
      isNotificationShown: false,
      notificationMessage: '',
      freeDeliveryMessage: ''
    };
  },
  computed: {
    ...mapState({
      isCartOpen: types.state.IS_CART_OPEN
    }),
    ...mapGetters({
      hasFreeDelivery: types.getters.HAS_FREE_DELIVERY,
      getFreeDeliveryMessageConfig: types.getters.GET_FREE_DELIVERY_MESSAGE_CONFIG
    })
  },
  watch: {
    isCartOpen: 'hideNotification'
  },
  created() {
    NotificationService.subscribe(
      NotificationService.NOTIFICATION_NAME.PRODUCT_ADDED_TO_CART,
      this.showAddedToCartNotification
    );
    NotificationService.subscribe(
      NotificationService.NOTIFICATION_NAME.GENERIC_FORMULA_SWITCHED_IN_CART,
      this.showFormulaSwitchedInCartNotification
    );
  },
  beforeDestroy() {
    NotificationService.unsubscribe(
      NotificationService.NOTIFICATION_NAME.PRODUCT_ADDED_TO_CART,
      this.showAddedToCartNotification
    );
    NotificationService.unsubscribe(
      NotificationService.NOTIFICATION_NAME.GENERIC_FORMULA_SWITCHED_IN_CART,
      this.showFormulaSwitchedInCartNotification
    );
  },
  methods: {
    ...mapMutations({
      setIsCartOpen: types.mutations.SET_IS_CART_OPEN
    }),
    onOpenCart() {
      this.hideNotification();
      this.setIsCartOpen(true);
    },
    clearNotificationTimeout() {
      if (this.addToCartNotificationTimeout) {
        clearTimeout(this.addToCartNotificationTimeout);
        this.addToCartNotificationTimeout = null;
      }
    },
    setFreeDeliveryMessage() {
      if (!this.hasFreeDelivery) {
        const { messageKey, deliveryData } = this.getFreeDeliveryMessageConfig();
        const { residue, deliveryCountry } = deliveryData;
        const countryLabel = this.getCountryDisplayName(deliveryCountry);
        const residueFormatted = this.formatPrice(residue);

        this.freeDeliveryMessage = this.$t(messageKey, [residueFormatted, countryLabel]);
        return;
      }

      if (this.freeDeliveryMessage !== '') {
        this.freeDeliveryMessage = '';
      }
    },
    showAddedToCartNotification(productName) {
      const productLabel = this.$t(PRODUCT_LABEL[productName]);
      const message = this.$t('cart.productAddedToCartMessage', [productLabel]);

      this.showNotification(message);
    },
    showFormulaSwitchedInCartNotification(newFormulaId) {
      const newFormulaLabel = this.$t(EYE_SERUM_GENERIC_FORMULA_LABELS[newFormulaId]);
      const message = this.$t('section.eyeContourSerum.generic.switchFormulaMessage', [
        newFormulaLabel
      ]);

      this.showNotification(message);
    },
    showNotification(message) {
      this.clearNotificationTimeout();

      this.notificationMessage = message;

      this.setFreeDeliveryMessage();

      this.addToCartNotificationTimeout = setTimeout(() => {
        this.hideNotification();
      }, LONG_NOTIFICATION_DURATION);

      this.isNotificationShown = true;
    },
    hideNotification() {
      this.clearNotificationTimeout();
      this.isNotificationShown = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/colors.scss';

.add-to-cart-notification {
  max-width: 375px;
  width: 90vw;
  position: fixed;
  z-index: 2000;
  top: 16px;
  right: 16px;
}

.delivery-notification {
  margin-top: 14px;
  padding-top: 8px;
  border-top: 1px solid #626773;
}

@media (min-width: 600px) {
  .add-to-cart-notification {
    max-width: 476px;
    top: 100px;
    right: 45px;
  }
}
</style>
